import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { DatePickerView } from '../types.js';
import type { CalendarCell, MonthGrid } from './types.js';

// ─────────────────────────────────────────────────────────────────────────────
// CalendarHost — what CalendarController reads from its host
// ─────────────────────────────────────────────────────────────────────────────

export interface CalendarHost extends ReactiveControllerHost {
  minDate:        Date | null;
  maxDate:        Date | null;
  disabledDates:  Date[];
  disabledDays:   number[];
  numberOfMonths: number;
  firstDayOfWeek: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CalendarController
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages all calendar grid computation and panel navigation.
 * Holds no DOM references — calls host.requestUpdate() to trigger re-renders.
 */
export class CalendarController implements ReactiveController {
  readonly host: CalendarHost;

  // ── Navigation state ─────────────────────────────────────────────
  viewYear:    number;
  viewMonth:   number;        // 0-indexed
  activeView:  DatePickerView = 'date';
  focusedDate: Date | null    = null; // roving tabindex anchor; null = no keyboard nav active

  constructor(host: CalendarHost) {
    this.host = host;
    host.addController(this);

    const now      = new Date();
    this.viewYear  = now.getFullYear();
    this.viewMonth = now.getMonth();
  }

  hostConnected(): void { /* no-op */ }
  hostDisconnected(): void { /* no-op */ }

  // ── Navigation mutations ─────────────────────────────────────────

  navigateMonth(delta: -1 | 1): void {
    const step = delta * Math.max(1, this.host.numberOfMonths);
    let m = this.viewMonth + step;
    let y = this.viewYear;
    while (m > 11) { m -= 12; y++; }
    while (m < 0)  { m += 12; y--; }
    this.viewMonth = m;
    this.viewYear  = y;
    this.host.requestUpdate();
  }

  navigateYear(delta: -1 | 1): void {
    this.viewYear += delta;
    this.host.requestUpdate();
  }

  setViewMonth(month: number): void {
    this.viewMonth = month;
    this.host.requestUpdate();
  }

  setViewYear(year: number): void {
    this.viewYear = year;
    this.host.requestUpdate();
  }

  switchView(view: DatePickerView): void {
    this.activeView = view;
    this.host.requestUpdate();
  }

  /**
   * Move keyboard focus by one step in the given direction.
   * Auto-navigates the view when focus leaves the visible month(s).
   * @param fallback  date to start from when focusedDate is null (typically today or selected date)
   */
  moveFocus(
    dir: 'left' | 'right' | 'up' | 'down' | 'home' | 'end',
    fallback: Date,
  ): void {
    const current = this.focusedDate ?? fallback;
    const next    = new Date(current);

    switch (dir) {
      case 'left':  next.setDate(next.getDate() - 1); break;
      case 'right': next.setDate(next.getDate() + 1); break;
      case 'up':    next.setDate(next.getDate() - 7); break;
      case 'down':  next.setDate(next.getDate() + 7); break;
      case 'home': {
        const offset = ((next.getDay() - (this.host.firstDayOfWeek % 7)) + 7) % 7;
        next.setDate(next.getDate() - offset);
        break;
      }
      case 'end': {
        const offset = ((next.getDay() - (this.host.firstDayOfWeek % 7)) + 7) % 7;
        next.setDate(next.getDate() + (6 - offset));
        break;
      }
    }

    this.focusedDate = next;

    // Auto-navigate the view when focus leaves the visible range
    if (!this._isDateVisible(next)) {
      this.viewMonth = next.getMonth();
      this.viewYear  = next.getFullYear();
    }

    this.host.requestUpdate();
  }

  /**
   * Move keyboard focus to the same day-of-month in the adjacent month (PageUp/Down).
   * Clamps to the last day of the target month when needed.
   */
  moveFocusMonth(delta: -1 | 1, fallback: Date): void {
    const fd       = this.focusedDate ?? fallback;
    let newMonth   = fd.getMonth() + delta;
    let newYear    = fd.getFullYear();
    while (newMonth > 11) { newMonth -= 12; newYear++; }
    while (newMonth < 0)  { newMonth += 12; newYear--; }
    const maxDay        = new Date(newYear, newMonth + 1, 0).getDate();
    this.focusedDate    = new Date(newYear, newMonth, Math.min(fd.getDate(), maxDay));
    this.viewMonth      = this.focusedDate.getMonth();
    this.viewYear       = this.focusedDate.getFullYear();
    this.host.requestUpdate();
  }

  // ── Grid builders ────────────────────────────────────────────────

  getMonthGrids(): MonthGrid[] {
    const grids: MonthGrid[] = [];
    const count = Math.max(1, this.host.numberOfMonths);
    for (let i = 0; i < count; i++) {
      let m = this.viewMonth + i;
      let y = this.viewYear;
      while (m > 11) { m -= 12; y++; }
      grids.push(this._buildMonthGrid(y, m));
    }
    return grids;
  }

  /** 3×4 grid of month buttons for the month-picker panel view. */
  getMonthPickerRows(): { label: string; short: string; value: number; disabled: boolean }[][] {
    const rows: { label: string; short: string; value: number; disabled: boolean }[][] = [];
    const months = this._monthNames();
    for (let row = 0; row < 4; row++) {
      const cells = [];
      for (let col = 0; col < 3; col++) {
        const m = row * 3 + col;
        cells.push({
          label:    months.long[m],
          short:    months.short[m],
          value:    m,
          disabled: this._isMonthDisabled(m, this.viewYear),
        });
      }
      rows.push(cells);
    }
    return rows;
  }

  /** Decade year grid (10 years per page). */
  getYearPickerRows(base: number): { label: string; value: number; disabled: boolean }[][] {
    const rows: { label: string; value: number; disabled: boolean }[][] = [];
    for (let row = 0; row < 5; row++) {
      const cells = [];
      for (let col = 0; col < 2; col++) {
        const y = base + row * 2 + col;
        cells.push({
          label:    String(y),
          value:    y,
          disabled: this._isYearDisabled(y),
        });
      }
      rows.push(cells);
    }
    return rows;
  }

  /** Short day-of-week header labels, starting at firstDayOfWeek. */
  getDayHeaders(): { label: string; long: string }[] {
    const short = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const long  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const start = this.host.firstDayOfWeek % 7;
    const result = [];
    for (let i = 0; i < 7; i++) {
      const idx = (start + i) % 7;
      result.push({ label: short[idx], long: long[idx] });
    }
    return result;
  }

  // ── Private grid math ────────────────────────────────────────────

  private _buildMonthGrid(year: number, month: number): MonthGrid {
    const start      = this.host.firstDayOfWeek % 7;
    const firstDay   = new Date(year, month, 1);
    const lastDay    = new Date(year, month + 1, 0);
    const today      = new Date();

    // Day-of-week index of the 1st — offset to firstDayOfWeek
    const startOffset = ((firstDay.getDay() - start) + 7) % 7;
    const totalDays   = lastDay.getDate();

    const cells: CalendarCell[] = [];

    // Prefix padding from previous month
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      cells.push(this._makeCell(d, today, true));
    }

    // Days of this month
    for (let d = 1; d <= totalDays; d++) {
      cells.push(this._makeCell(new Date(year, month, d), today, false));
    }

    // Suffix padding to fill 6 full rows (42 cells)
    const needed = 42 - cells.length;
    for (let d = 1; d <= needed; d++) {
      cells.push(this._makeCell(new Date(year, month + 1, d), today, true));
    }

    // Chunk into weeks
    const weeks: CalendarCell[][] = [];
    for (let i = 0; i < 6; i++) {
      weeks.push(cells.slice(i * 7, i * 7 + 7));
    }

    return { year, month, weeks };
  }

  private _makeCell(date: Date, today: Date, otherMonth: boolean): CalendarCell {
    return {
      date,
      dayNum:     date.getDate(),
      otherMonth,
      today:      this._isSameDay(date, today),
      disabled:   this._isCellDisabled(date),
    };
  }

  private _isCellDisabled(date: Date): boolean {
    const { minDate, maxDate, disabledDates, disabledDays } = this.host;
    if (minDate && date < this._startOfDay(minDate)) return true;
    if (maxDate && date > this._startOfDay(maxDate)) return true;
    if (disabledDays.includes(date.getDay())) return true;
    if (disabledDates.some(d => this._isSameDay(d, date))) return true;
    return false;
  }

  private _isMonthDisabled(month: number, year: number): boolean {
    const { minDate, maxDate } = this.host;
    const lastOfMonth  = new Date(year, month + 1, 0);
    const firstOfMonth = new Date(year, month, 1);
    if (minDate && lastOfMonth  < this._startOfDay(minDate)) return true;
    if (maxDate && firstOfMonth > this._startOfDay(maxDate)) return true;
    return false;
  }

  private _isYearDisabled(year: number): boolean {
    const { minDate, maxDate } = this.host;
    if (minDate && year < minDate.getFullYear()) return true;
    if (maxDate && year > maxDate.getFullYear()) return true;
    return false;
  }

  // ── Date helpers ─────────────────────────────────────────────────

  private _isDateVisible(date: Date): boolean {
    const count = Math.max(1, this.host.numberOfMonths);
    for (let i = 0; i < count; i++) {
      let m = this.viewMonth + i;
      let y = this.viewYear;
      while (m > 11) { m -= 12; y++; }
      if (date.getFullYear() === y && date.getMonth() === m) return true;
    }
    return false;
  }

  private _isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth()    === b.getMonth()
        && a.getDate()     === b.getDate();
  }

  private _startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private _monthNames(): { long: string[]; short: string[] } {
    const long  = [];
    const short = [];
    for (let m = 0; m < 12; m++) {
      const d = new Date(2000, m, 1);
      long.push( d.toLocaleString('en', { month: 'long' }));
      short.push(d.toLocaleString('en', { month: 'short' }));
    }
    return { long, short };
  }
}
